import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Clock,
  User,
  CheckCircle,
  Circle,
  Trash2,
  Edit,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import { toast } from 'react-hot-toast';
import {
  usePatientTasks,
  useCreateTask,
  useUpdateTask,
  useDeleteTask,
  type TaskStatus,
  type TaskPriority,
  type TaskCategory,
  type PatientTask,
} from '@/hooks/usePatientTasks';
import { useClinicUsersList } from '@/hooks/useClinicUsers';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';

interface TaskTabProps {
  patientId: string;
  clinicId: string;
}

export function TaskTab({ patientId, clinicId }: TaskTabProps) {
  const { t } = useTranslation();
  const { data: tasks, isLoading, error } = usePatientTasks(patientId);
  const { data: clinicUsers } = useClinicUsersList(clinicId);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTask, setEditingTask] = useState<PatientTask | null>(null);
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'all'>('all');

  const filtered = tasks?.filter((task) => statusFilter === 'all' || task.status === statusFilter) ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
        <p className="text-sm text-red-800 dark:text-red-200">
          {t('tasks.error_loading', 'Failed to load tasks')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {t('tasks.title', 'Tasks & Follow-ups')}
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('tasks.subtitle', 'Manage patient tasks and follow-up reminders')}
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t('tasks.create', 'New Task')}
        </Button>
      </div>

      {/* Status Filter */}
      <div className="flex flex-wrap gap-2">
        {(['all', 'pending', 'in_progress', 'completed', 'cancelled'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              statusFilter === status
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            {t(`tasks.filter.${status}`, status === 'all' ? 'All' : status)}
          </button>
        ))}
      </div>

      {/* Tasks List */}
      {filtered.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-12 text-center dark:border-gray-700 dark:bg-card-dark">
          <CheckCircle className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-sm font-medium text-gray-900 dark:text-white">
            {t('tasks.empty_title', 'No tasks found')}
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {statusFilter === 'all'
              ? t('tasks.empty_description', 'Create a task to get started')
              : t('tasks.empty_filtered', `No ${statusFilter} tasks`)}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onEdit={() => setEditingTask(task)}
              clinicId={clinicId}
              patientId={patientId}
            />
          ))}
        </div>
      )}

      {/* Create Task Modal */}
      {showCreateModal && (
        <TaskFormModal
          onClose={() => setShowCreateModal(false)}
          clinicId={clinicId}
          patientId={patientId}
          clinicUsers={clinicUsers ?? []}
        />
      )}

      {/* Edit Task Modal */}
      {editingTask && (
        <TaskFormModal
          task={editingTask}
          onClose={() => setEditingTask(null)}
          clinicId={clinicId}
          patientId={patientId}
          clinicUsers={clinicUsers ?? []}
        />
      )}
    </div>
  );
}

interface TaskCardProps {
  task: PatientTask;
  onEdit: () => void;
  clinicId: string;
  patientId: string;
}

function TaskCard({ task, onEdit, patientId }: TaskCardProps) {
  const { t } = useTranslation();
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();

  const handleToggleComplete = async () => {
    try {
      await updateTask.mutateAsync({
        task_id: task.id,
        patient_id: patientId,
        status: task.status === 'completed' ? 'pending' : 'completed',
      });
      toast.success(
        task.status === 'completed'
          ? t('tasks.toast_reopened', 'Task reopened')
          : t('tasks.toast_completed', 'Task completed')
      );
    } catch (error) {
      toast.error(t('tasks.error_update', 'Failed to update task'));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('tasks.confirm_delete', 'Are you sure you want to delete this task?'))) {
      return;
    }
    try {
      await deleteTask.mutateAsync({ task_id: task.id, patient_id: patientId });
      toast.success(t('tasks.toast_deleted', 'Task deleted'));
    } catch (error) {
      toast.error(t('tasks.error_delete', 'Failed to delete task'));
    }
  };

  const getStatusIcon = (status: TaskStatus) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'in_progress':
        return <Circle className="h-5 w-5 text-blue-500 fill-blue-500" />;
      case 'cancelled':
        return <Circle className="h-5 w-5 text-red-500" />;
      default:
        return <Circle className="h-5 w-5 text-gray-400" />;
    }
  };

  const getPriorityBadge = (priority: TaskPriority) => {
    const colors = {
      urgent: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200',
      high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200',
      medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200',
      low: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    };

    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${colors[priority]}`}>
        {t(`tasks.priority.${priority}`, priority)}
      </span>
    );
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-card-dark">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <button
            onClick={handleToggleComplete}
            disabled={updateTask.isPending}
            className="mt-0.5 flex-shrink-0 disabled:opacity-50"
          >
            {getStatusIcon(task.status)}
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h4
                className={`text-sm font-medium ${
                  task.status === 'completed'
                    ? 'line-through text-gray-500 dark:text-gray-400'
                    : 'text-gray-900 dark:text-white'
                }`}
              >
                {task.title}
              </h4>
              {getPriorityBadge(task.priority)}
            </div>

            {task.description && (
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{task.description}</p>
            )}

            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
              {task.assigned_to_name && (
                <div className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  <span>{task.assigned_to_name}</span>
                </div>
              )}

              {task.due_date && (
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <span>{format(new Date(task.due_date), 'dd/MM/yyyy', { locale: da })}</span>
                  {new Date(task.due_date) < new Date() && task.status !== 'completed' && (
                    <AlertCircle className="h-3 w-3 text-red-500" />
                  )}
                </div>
              )}

              <span>
                {t('tasks.created', 'Created')}{' '}
                {format(new Date(task.created_at), 'dd/MM/yyyy', { locale: da })}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onEdit}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            title={t('common.edit', 'Edit')}
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={handleDelete}
            disabled={deleteTask.isPending}
            className="text-gray-400 hover:text-red-600 disabled:opacity-50"
            title={t('common.delete', 'Delete')}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

interface TaskFormModalProps {
  task?: PatientTask;
  onClose: () => void;
  clinicId: string;
  patientId: string;
  clinicUsers: Array<{ user_id: string; user: { name: string | null; email: string } | null }>;
}

function TaskFormModal({ task, onClose, clinicId, patientId, clinicUsers }: TaskFormModalProps) {
  const { t } = useTranslation();
  const createTask = useCreateTask();
  const updateTask = useUpdateTask();
  const isEditing = Boolean(task);

  const [formData, setFormData] = useState({
    title: task?.title ?? '',
    description: task?.description ?? '',
    priority: (task?.priority ?? 'medium') as TaskPriority,
    category: (task?.category ?? 'patient_care') as TaskCategory,
    status: (task?.status ?? 'pending') as TaskStatus,
    assigned_to: task?.assigned_to ?? '',
    due_date: task?.due_date ? format(new Date(task.due_date), 'yyyy-MM-dd') : '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (isEditing && task) {
        await updateTask.mutateAsync({
          task_id: task.id,
          patient_id: patientId,
          ...formData,
          due_date: formData.due_date || null,
          assigned_to: formData.assigned_to || null,
        });
        toast.success(t('tasks.toast_updated', 'Task updated'));
      } else {
        await createTask.mutateAsync({
          clinic_id: clinicId,
          patient_id: patientId,
          title: formData.title,
          description: formData.description || null,
          priority: formData.priority,
          category: formData.category,
          assigned_to: formData.assigned_to || null,
          due_date: formData.due_date || null,
        });
        toast.success(t('tasks.toast_created', 'Task created'));
      }
      onClose();
    } catch (error) {
      toast.error(
        isEditing
          ? t('tasks.error_update', 'Failed to update task')
          : t('tasks.error_create', 'Failed to create task')
      );
    }
  };

  return (
    <Modal
      title={isEditing ? t('tasks.edit_title', 'Edit Task') : t('tasks.create_title', 'Create Task')}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            {t('tasks.field_title', 'Title')} *
          </label>
          <input
            type="text"
            required
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            placeholder={t('tasks.placeholder_title', 'e.g., Follow up on treatment')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            {t('tasks.field_description', 'Description')}
          </label>
          <textarea
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            placeholder={t('tasks.placeholder_description', 'Additional details...')}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              {t('tasks.field_priority', 'Priority')}
            </label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value as TaskPriority })}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="low">{t('tasks.priority.low', 'Low')}</option>
              <option value="medium">{t('tasks.priority.medium', 'Medium')}</option>
              <option value="high">{t('tasks.priority.high', 'High')}</option>
              <option value="urgent">{t('tasks.priority.urgent', 'Urgent')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              {t('tasks.field_category', 'Category')}
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value as TaskCategory })}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="patient_care">{t('tasks.category.patient_care', 'Patient Care')}</option>
              <option value="administrative">{t('tasks.category.administrative', 'Administrative')}</option>
              <option value="follow_up">{t('tasks.category.follow_up', 'Follow-up')}</option>
              <option value="documentation">{t('tasks.category.documentation', 'Documentation')}</option>
            </select>
          </div>
        </div>

        {isEditing && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              {t('tasks.field_status', 'Status')}
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as TaskStatus })}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="pending">{t('tasks.status.pending', 'Pending')}</option>
              <option value="in_progress">{t('tasks.status.in_progress', 'In Progress')}</option>
              <option value="completed">{t('tasks.status.completed', 'Completed')}</option>
              <option value="cancelled">{t('tasks.status.cancelled', 'Cancelled')}</option>
            </select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              {t('tasks.field_assigned_to', 'Assign to')}
            </label>
            <select
              value={formData.assigned_to}
              onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="">{t('tasks.unassigned', 'Unassigned')}</option>
              {clinicUsers.map((member) => (
                <option key={member.user_id} value={member.user_id}>
                  {member.user?.name ?? member.user?.email ?? 'Unknown'}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              {t('tasks.field_due_date', 'Due Date')}
            </label>
            <input
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={createTask.isPending || updateTask.isPending}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button type="submit" disabled={createTask.isPending || updateTask.isPending}>
            {createTask.isPending || updateTask.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('common.saving', 'Saving...')}
              </>
            ) : isEditing ? (
              t('common.save', 'Save')
            ) : (
              t('tasks.create', 'Create')
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
